import { AnimatePresence, Variants, motion } from 'framer-motion';
import { Fragment, useMemo } from 'react';
import { Box, Button, Center, Column, Portal, Row, Text, ZStack } from '~/commonComponents';
import { IconButton } from '~/commonComponents/IconButton';
import { ModalOrder, useModal } from '~/components/ModalQueue';
import { getShouldUseNewOnboardingPopup } from '~/growthbook';
import { CloseIcon } from '~/images/icons';
import { Colors } from '~/themes/colors';
import { useOfficeTutorial } from './Hooks';
import { TTutorialStepItem } from './Types';

const variants: Variants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 100 : -100,
    opacity: 0,
    position: 'absolute' as const,
  }),
  center: {
    x: 0,
    opacity: 1,
    position: 'relative' as const,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 100 : -100,
    opacity: 0,
    position: 'absolute' as const,
  }),
};

export const OfficeTutorialPopup = () => {
  const {
    activeStep: [index, direction],
    tutorial,
    tutorialSteps,
    onNextSlide,
    onPrevSlide,
    onClose,
  } = useOfficeTutorial();

  const hasInitialImage = !!tutorialSteps?.initialImages;
  const isGettingStarted = index === -1 && hasInitialImage;
  const isLastSlide = tutorialSteps?.images && index === tutorialSteps?.images.length - 1;

  const currentItem: TTutorialStepItem | undefined = useMemo(() => {
    if (isGettingStarted) {
      return tutorialSteps.initialImages;
    }

    return tutorialSteps?.images[index];
  }, [index, isGettingStarted, tutorialSteps]);

  const totalSlides: number = useMemo(
    () => tutorialSteps?.images?.length || 0,
    [tutorialSteps?.images],
  );

  const { isActive } = useModal({
    id: ModalOrder.TUTORIAL_POPUP,
    condition: !!currentItem && !!tutorial,
  });

  if (!currentItem || !tutorial) {
    return null;
  }

  if (getShouldUseNewOnboardingPopup() && !isActive) {
    return null;
  }

  return (
    <Portal>
      <div className="modal" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
        <Box height={570} position="relative" data-cy={tutorial.id}>
          <ZStack top={-16} right={-16} zIndex={1000}>
            <IconButton
              Icon={<CloseIcon color={Colors.white} />}
              buttonStyle={{
                backgroundColor: Colors.secondary_500,
                borderRadius: '100%',
                height: 36,
                width: 36,
                boxShadow: 'rgba(0, 0, 0, 0.4) -1px 1px 8px 0px',
              }}
              onClick={onClose}
              dataCy="close-tutorial-popup-button"
            />
          </ZStack>
          <Column
            backgroundColor={Colors.white}
            borderRadius={12}
            height={570}
            overflowY="auto"
            overflowX="hidden"
            width={560}
            position="relative"
          >
            <Center
              position="relative"
              width="100%"
              height={317}
              background="linear-gradient(180deg, #4FC0CF 0%, #4FCFC0 100%)"
            >
              <AnimatePresence custom={direction} mode="wait">
                <motion.img
                  key={currentItem.url}
                  src={currentItem.url}
                  alt={currentItem.title}
                  custom={direction}
                  variants={variants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.4 }}
                  style={{
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'bottom',
                    width: 524,
                  }}
                />
              </AnimatePresence>
            </Center>
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={`${currentItem.title}-${currentItem.description}`}
                custom={direction}
                variants={variants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.4 }}
              >
                <Column gap={16} paddingInline={20}>
                  <Row justifyContent={isGettingStarted ? 'center' : 'start'} paddingTop={28}>
                    {!isGettingStarted && (
                      <Text bold color={Colors.primary_500} textAlign="center">
                        {index + 1} of {totalSlides} -&nbsp;
                      </Text>
                    )}
                    <Text bold color={Colors.primary_500} textAlign="center">
                      {currentItem.title}
                    </Text>
                  </Row>

                  <Text color={Colors.neutral_500} textAlign={isGettingStarted ? 'center' : 'left'}>
                    {currentItem.description.split('\n').map((line, index) => (
                      <Fragment key={index}>
                        {line}
                        <br />
                      </Fragment>
                    ))}
                  </Text>
                </Column>
              </motion.div>
            </AnimatePresence>
            <Row justifyContent="center" gap={16} marginTop="auto" paddingBottom={32}>
              {isGettingStarted ? (
                <Button
                  color="primary"
                  text="Let's Get Started"
                  textStyle={{ fontSize: 18 }}
                  size="small"
                  onClick={onNextSlide}
                  dataCy="tutorial-get-started-button"
                />
              ) : (
                <>
                  <Button
                    color="secondary"
                    style={{
                      backgroundColor: Colors.secondary_500,
                      width: 152,
                      height: 48,
                    }}
                    text="Back"
                    textStyle={{ fontSize: 18 }}
                    size="small"
                    onClick={onPrevSlide}
                    dataCy="tutorial-back-button"
                  />
                  <Button
                    color="primary"
                    style={{
                      width: 152,
                      height: 48,
                    }}
                    text={isLastSlide ? 'Close' : 'Next'}
                    textStyle={{ fontSize: 18 }}
                    size="small"
                    onClick={onNextSlide}
                    dataCy={isLastSlide ? 'tutorial-close-button' : 'tutorial-next-button'}
                  />
                </>
              )}
            </Row>
          </Column>
        </Box>
      </div>
    </Portal>
  );
};
